"use client";

import ControllerWarpper from "@/components/common/ControllerWarpper";
import { Alert, Col, Form, Input, Modal, Row, Tag, Typography } from "antd";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { validatePhoneNumber } from "@/utils/validates/validatePhoneNumber";
import { useAppDispatch } from "@/lib/redux/hooks";
import { thunks } from "@/lib/redux/thunks";
import { toast } from "react-toastify";
import { TypeFlight } from "@/lib/redux/features/flight/type";
import { useTranslation } from "react-i18next";
import { getDatetimeSegment } from "@/utils/flight/getDatetimeSegment";
import { useTETranslation } from "@/hooks/app/useTETranslation";
import { promotionFixedDiscountRoutesVJ } from "@/constants/flight/fixedPricesRoutes";

export type ModalContactNowRef = {
  open: (flight?: TypeFlight) => void;
  close: () => void;
};

type ModalContactNowProps = {
  route?: string
  discountAmount?: number
}

const schema = yup.object({
  name: yup.string().required("booking.fullname_input_required_message").trim(),
  phone: yup.string()
    .required("booking.phonenumber_input_required_message")
    .test(
      "contact.phone",
      "booking.phonenumber_input_rule_message",
      validatePhoneNumber
    ),
}).required()

const ModalContactNow = forwardRef<ModalContactNowRef, ModalContactNowProps>(({ route, discountAmount }, ref) => {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false);
  const [flight, setFlight] = useState<TypeFlight | null>(null);
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const { getFormatCurrency } = useTETranslation()

  const resolvedDiscount = discountAmount
    ?? (route ? promotionFixedDiscountRoutesVJ[route] : undefined)
    ?? flight?.inventories?.[0]?.promotionVJDiscount

  const dispatch = useAppDispatch()

  const handleSubmit = async (data: any) => {
    try {
      let startDate,
        route,
        flightNumber
      if (!!flight) {
        const firstSeg = flight.segments[0]
        const lastSeg = flight.segments[flight.segments.length - 1]
        startDate = getDatetimeSegment(firstSeg.startTime, firstSeg.startTimeZoneOffset).format('DD-MM-YYYY')
        route = `${firstSeg.startPoint}${lastSeg.endPoint}`
        flightNumber = flight.segments.map(seg => seg.flightNumber).join(', ')
      }
      await dispatch(thunks.customer_temp({
        body: {
          TYPE: 'JSON',
          fullName: data.name,
          phoneNumber: data.phone,
          type: 'hotline_form',
          title: `VJ PROMOTION${flight ? ` - ${startDate} - ${route} - ${flightNumber}` : ''}`
        }
      })).unwrap()
      toast.success(t("booking.send_contact_successful"));
    } catch (error) {
      toast.error(t("booking.send_contact_failure"));
    }

    setVisible(false);
  }

  useImperativeHandle(ref, () => ({
    open: (_flight) => {
      setFlight(_flight || null)
      setVisible(true)
    },
    close: () => setVisible(false),
  }));

  return (
    <Modal
      open={visible}
      onCancel={() => setVisible(false)}
      okText={t("common.submit")}
      title={t("flight.modal_contact.title")}
      onOk={form.handleSubmit(handleSubmit)}
    >
      <Row style={{ marginBottom: '1rem' }}>
        <Col span={24}>
          <Alert
            showIcon
            title={t("flight.modal_contact.alert_message", { hotline: process.env.NEXT_PUBLIC_COMPANY_PHONE || "", price: getFormatCurrency(resolvedDiscount || 0, true) })}
          />
        </Col>
      </Row>
      <Form
        layout='vertical'
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit(handleSubmit)();
          }}
        >
          <ControllerWarpper
            required
            label={t("common.fullname")}
            name="name"
            control={form.control}
            render={({ field }) => {
              return (
                <Input placeholder={t("common.fullname_placeholder")} {...field} />
              )
            }}
          />
          <ControllerWarpper
            required
            label={t("common.phonenumber")}
            name="phone"
            control={form.control}
            render={({ field }) => {
              return (
                <Input placeholder={t("common.phonenumber_placeholder")} {...field} />
              )
            }}
          />
        </form>
      </Form>
    </Modal>
  )
})

export default ModalContactNow;